import { ActionType, createReducer } from 'typesafe-actions';
import { combineReducers } from 'redux';
import { ReviewsStore, ReviewsSort, ReviewsByScoreFilterRecord, ReviewsPaginationPage } from '../types';
import {
  ADD_FILTER_BY_SCORE,
  FETCH_REVIEWS_NEXT_PAGE,
  FETCH_REVIEWS_PAGE,
  REMOVE_FILTER_BY_SCORE,
  REVIEWS_RESET_FILTER_BY_SCORE,
  REVIEWS_RESET_SORTING,
  REVIEWS_SORTING_SET,
} from '../actions';
import { photosModalReducer } from './photos-modal';
import { likeDislikeReducer } from './like-dislike-reducer';
import { postReviewReducer } from './post-reviews';
import { photoFilesCacheReducer, selectedPhotosReducer } from './upload-photos';

const reviewsSortingInitial = (): ReviewsSort => 'helpfulness:desc';
const filtersByScoreInitial = (): ReviewsByScoreFilterRecord[] => [
  { score: 5, total: 0, isSet: false },
  { score: 4, total: 0, isSet: false },
  { score: 3, total: 0, isSet: false },
  { score: 2, total: 0, isSet: false },
  { score: 1, total: 0, isSet: false },
];

type ReviewsReducerActions =
  | ActionType<typeof FETCH_REVIEWS_PAGE.failure>
  | ActionType<typeof FETCH_REVIEWS_PAGE.success>;
export const reviews = createReducer<ReviewsStore | null, ReviewsReducerActions>(null)
  .handleAction([FETCH_REVIEWS_PAGE.failure], () => null)
  .handleAction(FETCH_REVIEWS_PAGE.success, (_, { payload }) => payload);

type ReviewsErrorReducerActions =
  | ActionType<typeof FETCH_REVIEWS_PAGE.failure>
  | ActionType<typeof FETCH_REVIEWS_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_PAGE.success>;
const reviewsError = createReducer<Error | null, ReviewsErrorReducerActions>(null)
  .handleAction([FETCH_REVIEWS_PAGE.failure], (_, { payload }) => payload)
  .handleAction([FETCH_REVIEWS_PAGE.request, FETCH_REVIEWS_PAGE.success], () => null);

type ReviewsLoadingReducerActions =
  | ActionType<typeof FETCH_REVIEWS_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_PAGE.success>
  | ActionType<typeof FETCH_REVIEWS_PAGE.failure>
  | ActionType<typeof FETCH_REVIEWS_PAGE.cancel>;
const reviewsLoading = createReducer<boolean, ReviewsLoadingReducerActions>(false)
  .handleAction([FETCH_REVIEWS_PAGE.request], () => true)
  .handleAction(
    [FETCH_REVIEWS_PAGE.success, FETCH_REVIEWS_PAGE.failure, FETCH_REVIEWS_PAGE.cancel],
    () => false,
  );

type ReviewsSortingReducerActions =
  | ActionType<typeof REVIEWS_SORTING_SET>
  | ActionType<typeof REVIEWS_RESET_SORTING>;
export const reviewsSorting = createReducer<ReviewsSort, ReviewsSortingReducerActions>(
  reviewsSortingInitial(),
)
  .handleAction(REVIEWS_SORTING_SET, (_, { payload }) => payload)
  .handleAction(REVIEWS_RESET_SORTING, () => reviewsSortingInitial());

type ReviewsByScoreReducerActions =
  | ActionType<typeof FETCH_REVIEWS_PAGE.success>
  | ActionType<typeof ADD_FILTER_BY_SCORE>
  | ActionType<typeof REMOVE_FILTER_BY_SCORE>
  | ActionType<typeof REVIEWS_RESET_FILTER_BY_SCORE>;
export const byScoreReviewsFilters = createReducer<
  ReviewsByScoreFilterRecord[],
  ReviewsByScoreReducerActions
>(filtersByScoreInitial())
  .handleAction(FETCH_REVIEWS_PAGE.success, (state, { payload }) => {
    return state.map((record) => {
      const total =
        record.total === 0
          ? payload.facets.byScore.find((r) => r.score === record.score)?.total
          : record.total;
      return { ...record, total: total ?? 0 };
    });
  })
  .handleAction(ADD_FILTER_BY_SCORE, (state, { payload }) => {
    return state.map((record) => {
      return { ...record, isSet: record.score === payload ? true : record.isSet };
    });
  })
  .handleAction(REMOVE_FILTER_BY_SCORE, (state, { payload }) => {
    return state.map((record) => {
      return { ...record, isSet: record.score === payload ? false : record.isSet };
    });
  })
  .handleAction(REVIEWS_RESET_FILTER_BY_SCORE, () => filtersByScoreInitial());

type ReviewsPaginationReducerActions =
  | ActionType<typeof FETCH_REVIEWS_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.success>;
const reviewsPaginationPages = createReducer<ReviewsPaginationPage[], ReviewsPaginationReducerActions>([])
  .handleAction(FETCH_REVIEWS_PAGE.request, () => [])
  .handleAction(FETCH_REVIEWS_NEXT_PAGE.success, (state, { payload }) => {
    return [
      ...state,
      {
        reviewsCards: payload.reviewsCards,
        pageMeta: payload.pageMeta,
      },
    ];
  });

type ReviewsPaginationLoadingReducerActions =
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.cancel>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.failure>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.success>;
const reviewsPaginationPageIsLoading = createReducer<boolean, ReviewsPaginationLoadingReducerActions>(false)
  .handleAction(FETCH_REVIEWS_NEXT_PAGE.request, () => true)
  .handleAction(
    [
      FETCH_REVIEWS_PAGE.request,
      FETCH_REVIEWS_NEXT_PAGE.cancel,
      FETCH_REVIEWS_NEXT_PAGE.failure,
      FETCH_REVIEWS_NEXT_PAGE.success,
    ],
    () => false,
  );

type ReviewsPaginationLastPageLoadingErrorActions =
  | ActionType<typeof FETCH_REVIEWS_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.request>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.cancel>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.success>
  | ActionType<typeof FETCH_REVIEWS_NEXT_PAGE.failure>;
const reviewsPaginationLastPageLoadingError = createReducer<
  string | null,
  ReviewsPaginationLastPageLoadingErrorActions
>(null)
  .handleAction(
    [
      FETCH_REVIEWS_PAGE.request,
      FETCH_REVIEWS_NEXT_PAGE.request,
      FETCH_REVIEWS_NEXT_PAGE.cancel,
      FETCH_REVIEWS_NEXT_PAGE.success,
    ],
    () => null,
  )
  .handleAction(FETCH_REVIEWS_NEXT_PAGE.failure, (_, { payload }) => {
    return payload.message;
  });

export const reviewsRoot = combineReducers({
  reviews,
  reviewsError,
  reviewsLoading,
  reviewsSorting,
  reviewsFilters: combineReducers({
    byScore: byScoreReviewsFilters,
  }),
  pagination: reviewsPaginationPages,
  paginationLastPageError: reviewsPaginationLastPageLoadingError,
  paginationPageIsLoading: reviewsPaginationPageIsLoading,
  photosModal: photosModalReducer,
  opinions: likeDislikeReducer,
  postReview: postReviewReducer,
  selectedPhotos: selectedPhotosReducer,
  photosFileCache: photoFilesCacheReducer,
});
